import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useModules } from '@/api/base';
import type { ModuleFlags } from '@/api/base-types';
import { useLogout, useMe } from '@/api/queries';
import { GlobalSearch } from '@/features/base/shared/GlobalSearch';
import { MenuList } from '../kit/ActionMenu';
import { useStickyTableHeads } from '../kit/Table';
import { useToast } from '../kit/Toast';
import { useDismiss } from '../kit/useDismiss';

/**
 * Каркас экрана: меню слева, содержимое справа. На телефоне меню
 * уходит в нижнюю навигацию — одна и та же разметка, форму меняет
 * мобильный слой базы кита.
 */

interface Section {
  icon: string;
  label: string;
  /** Экран раздела. Нет экрана — нет и пункта меню */
  to: string | null;
  /** Флаг MODULE_*, которым выключается раздел готового модуля */
  module?: keyof ModuleFlags;
}

/**
 * Разделы — строго в этом порядке (дорожная карта, «Баги и доработки» № 2).
 * Раздел модуля, которого ещё нет, в меню не показывается: элементов
 * будущих модулей в интерфейсе нет совсем (ТЗ M2 §7.3). Модуль появится —
 * у раздела появится экран, и он встанет на своё место, ничего не сдвинув.
 */
const SECTIONS: Section[] = [
  { icon: 'i-home', label: 'Главная', to: null },
  { icon: 'i-cal', label: 'Мероприятия', to: '/events' },
  { icon: 'i-crm', label: 'CRM', to: '/crm', module: 'deals' },
  { icon: 'i-reg', label: 'Регистрации', to: '/registrations', module: 'registrations' },
  { icon: 'i-us', label: 'Клиенты', to: '/clients' },
  { icon: 'i-tm', label: 'Команды', to: '/teams' },
  { icon: 'i-br', label: 'Студии', to: '/studios' },
  { icon: 'i-bag', label: 'Финансы', to: null, module: 'payments' },
  { icon: 'i-task', label: 'Задачи', to: null, module: 'tasks' },
  { icon: 'i-us', label: 'Сотрудники', to: null },
  { icon: 'i-ch', label: 'Отчёты', to: null },
  { icon: 'i-gr', label: 'Настройки', to: null },
  { icon: 'i-sl', label: 'Коды входа', to: null },
];

/** Нижняя навигация кита — не больше пяти пунктов, пятый «Ещё» */
const BOTTOM_NAV_SECTIONS = 4;

export function Shell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();
  const me = useMe();
  const logout = useLogout();
  const modules = useModules();

  const [more, setMore] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const closeMore = useCallback(() => setMore(false), []);
  useDismiss(more, closeMore, moreRef);
  useStickyTableHeads();

  const sections = SECTIONS.filter(
    (s): s is Section & { to: string } =>
      s.to !== null && (!s.module || Boolean(modules.data?.[s.module])),
  );
  const bottom = sections.slice(0, BOTTOM_NAV_SECTIONS);
  const rest = sections.slice(BOTTOM_NAV_SECTIONS);

  const inEvents = pathname.startsWith('/events') || pathname.startsWith('/nominations');
  const isOn = (to: string) => (to === '/events' ? inEvents : pathname.startsWith(to));

  const signOut = () =>
    logout.mutate(undefined, {
      // Успех перезагружает страницу — навигация здесь не нужна
      onError: () => toast.showError('Не удалось выйти'),
    });

  return (
    <div className="ui app">
      <div className="nav">
        <div className="brand">
          <div className="bmark">YC</div>
          <div className="bname">YouChamp</div>
        </div>

        {/* Общий поиск по базе — над разделами (БЛ M2 5.14) */}
        <GlobalSearch />

        {sections.map(({ icon, label, to }) => (
          <div key={label} className={isOn(to) ? 'ni on' : 'ni'} onClick={() => navigate(to)}>
            <svg>
              <use href={`#${icon}`} />
            </svg>
            {label}
          </div>
        ))}

        {/* Кто вошёл и как выйти. Роль не показываем: она пока одна,
            и подпись «супер-администратор» под каждым именем
            ничего не сообщает (ТЗ §8). */}
        <div className="nlbl">{me.data?.fullName ?? 'Сеанс'}</div>
        <div className="ni" onClick={signOut}>
          <svg>
            <use href="#i-us" />
          </svg>
          Выйти
        </div>
      </div>

      {children}

      <div className="mnav">
        {bottom.map(({ icon, label, to }) => (
          <div key={label} className={isOn(to) ? 'mn on' : 'mn'} onClick={() => navigate(to)}>
            <svg>
              <use href={`#${icon}`} />
            </svg>
            {label}
          </div>
        ))}
        {/* «Ещё» — остальные разделы и выход: на телефоне бокового меню нет */}
        <div
          className={rest.some((s) => isOn(s.to)) ? 'mn on' : 'mn'}
          onClick={() => setMore(true)}
        >
          <svg>
            <use href="#i-dots" />
          </svg>
          Ещё
        </div>
      </div>

      {/* Шторка вне .mnav: у нижней навигации свой слой, и панель
          действий .abar легла бы поверх шторки */}
      {more ? (
        <div ref={moreRef}>
          <MenuList
            items={[
              ...rest.map((s) => ({ label: s.label, onClick: () => navigate(s.to) })),
              { label: 'Выйти', onClick: signOut },
            ]}
            onPick={closeMore}
          />
        </div>
      ) : null}
    </div>
  );
}
